import React, { useState, useMemo } from 'react';
// import { TouchableOpacity, TextInput as RNTextInput, Alert } from 'react-native';
// import { useRouter, Stack, useLocalSearchParams } from 'expo-router';
// import { Ionicons } from '@expo/vector-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import SearchIcon from '@mui/icons-material/Search';
import { Virtuoso } from 'react-virtuoso';
import { Strings } from '../../constants';
import Bubble from '../../assets/icons/Bubble';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';

import {
    CobuStack,
    CobuText,
    CobuImage,
    CobuTextAvatar,
    CobuAlert,
    CobuHeader,
    CobuBackButton,
} from '@cobu/ui';
// import { Member } from '@/types/groupMembers';
import { URLS } from '../../constants';
import { useTheme } from 'tamagui';
import { useGroupMembers } from '../../hooks/useGroupMembers';
import { useChannelList } from '../../hooks/useChannelList';

// const bubble = require('@/assets/images/msg-bubble-blue.png')

// Minimal Member type for this route
interface Member {
  uuid: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  profileImage?: string;
  role?: { name?: string };
}
interface MemberItemProps {
  member: Member;
  onUserPress: (userId: string) => void;
  currentUserId?: string;
  enableDM?: boolean;
  onDM?: (userId: string, displayName: string) => void;
}

function MemberItem({ member, onUserPress, currentUserId, enableDM, onDM }: MemberItemProps) {
  const strings = Strings.get();
  const theme = useTheme();

  const displayName = member.displayName || `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim();

  return (
    <CobuStack onPress={() => onUserPress(member.uuid)}>
      <CobuStack layout="horizontal" ai="center" p="$4" gap="$3">
        {/* Profile Image or Text Avatar */}
        {member.profileImage && member.profileImage !== URLS.DEFAULT_PROFILE_IMAGE ? (
          <CobuImage
            src={member.profileImage}
            width='$10'
            height='$10'
            borderRadius='$full'
          />
        ) : (
          <CobuTextAvatar name={displayName || strings.ui.members.unknownUser} uuid={member.uuid} />
        )}

        {/* Member Info */}
        <CobuStack f={1} gap="$1">
          <CobuText variant="body" fontSize='$4' fontWeight="bold" numberOfLines={1} color="$cobuDarkBlue">
            {displayName || strings.ui.members.unknownUser}
          </CobuText>
        </CobuStack>

        {/* DM Button */}
        {enableDM && currentUserId !== member.uuid && onDM && member.role?.name === 'RESIDENT' ? (
          <CobuStack
            p="$2"
            onPress={(e: any) => {
              e?.stopPropagation?.();
              onDM(member.uuid, displayName || '')
            }}
          >
            <Bubble size={24} color={theme.colorMuted?.val || '#9B9B9B'} />
          </CobuStack>
        ) : null}
      </CobuStack>
    </CobuStack>
  );
}

export default function AllMembersScreen() {
  const strings = Strings.get();
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const location = useLocation() as any;
  const theme = useTheme();
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertDescription, setAlertDescription] = useState('');
  const { navigateToChannel, isStreamAvailable } = useChannelList({ enabled: false });
  const { showDM } = useFeatureFlags();

  // Read navigation state (preferred on web to avoid URL bloat)
  const navState = (location?.state || {}) as {
    members?: any[];
    totalCount?: number;
    currentUserId?: string;
    enableDM?: boolean;
  };

  const buildingId = useSelector((s: RootState) => s.auth.user?.building?.uuid as string | undefined);
  const fallbackUserId = useSelector((s: RootState) => s.auth.user?.uuid as string | undefined);

  // Fetch all members if none were passed via navigation state
  const { members: hookMembers } = useGroupMembers({
    buildingId: navState?.members ? undefined : (buildingId || undefined),
    enabled: !navState?.members && !!buildingId,
    prioritizePictures: false,
    filterResidentsOnly: false,
  });

  // Base members list: prefer passed-in, otherwise hook results
  const baseMembers = useMemo(() => {
    const raw = (navState?.members || hookMembers || []) as any[];
    const toUpper = (s?: string) => (s || '').toUpperCase();
    const isPerkSpotMember = (member: any) => {
      const ln = toUpper(member?.lastName);
      return ln.includes('PERKSPOT') || ln.includes('PERKS AT WORK');
    };
    // Normalize to residents only like ChatIndex, and exclude deactivated/blocked
    return raw.filter((m: any) =>
      !m?.deactivated &&
      !m?.blocked &&
      (m?.role?.value === 0 || toUpper(m?.role?.name) === 'RESIDENT') &&
      !isPerkSpotMember(m)
    ) as Member[];
  }, [navState?.members, hookMembers]);

  const members: Member[] = baseMembers as Member[];
  const totalCount = useMemo(() => navState?.totalCount ?? members.length, [navState?.totalCount, members.length]);
  const currentUserId: string | undefined = navState?.currentUserId ?? fallbackUserId;
  const enableDM = navState?.enableDM ?? false;

  // Filter by search term and sort by name
  const filteredMembers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const filtered = members.filter((member) => {
      const name = (
        member.displayName || `${member.firstName ?? ''} ${member.lastName ?? ''}`
      )
        .trim()
        .toLowerCase();
      return term ? name.includes(term) : true;
    });

    // Sort by firstName, fallback to displayName
    return filtered.sort((a, b) => {
      const nameA = (a.firstName || a.displayName || '').toUpperCase();
      const nameB = (b.firstName || b.displayName || '').toUpperCase();
      return nameA.localeCompare(nameB);
    });
  }, [members, searchTerm]);

  const handleUserPress = (userId: string) => {
    setSearchTerm('');
    navigate(`/public-profile/${userId}`);
  };

  const handleDMPress = async (userId: string, displayName: string) => {
    try {
      setSearchTerm('');
      if (!isStreamAvailable) {
        setAlertTitle(strings.ui.allMembers.directMessages.unavailable);
        setAlertDescription(strings.ui.allMembers.directMessages.notAvailable);
        setAlertOpen(true);
        return;
      }
      const channel = await navigateToChannel(userId, displayName);
      const target = channel?.cid || channel?.id;
      if (target) {
        navigate(`/chat/${target}`);
      } else {
        setAlertTitle(strings.ui.allMembers.directMessages.unableToOpen);
        setAlertDescription(strings.ui.allMembers.directMessages.tryAgain);
        setAlertOpen(true);
      }
    } catch (err: any) {
      setAlertTitle(strings.ui.allMembers.directMessages.unableToOpen);
      setAlertDescription(err?.message || strings.ui.allMembers.directMessages.tryAgain);
      setAlertOpen(true);
    }
  };

  const renderHeader = () => (
    <CobuStack>
      {/* Top Title using CobuHeader */}
      <CobuHeader
        titleComponent={() => (
          <CobuStack ai="center" gap="$1">
            <CobuText variant="body" ta="center">
              {strings.discussion.allMembers.title}
            </CobuText>
            <CobuText
              variant="body"
              ta="center"
              m="$0"
              style={{ lineHeight: 14 }}
            >
              {totalCount} {strings.discussion.allMembers.subtitle}
              {totalCount > 1 ? 's' : ''}
            </CobuText>
          </CobuStack>
        )}
        renderLeft={() => <CobuBackButton onPress={() => navigate(-1)} />}
      />

      {/* Previous top bar kept for reference */}
      {/**
      <CobuStack bg="$cobuDarkBlue" pt="$4" pb="$2" px="$4">
        <CobuStack ai="center" f={1} gap="$1">
          <CobuText variant="heading" color="$white" fontSize='$3' fontWeight="bold" ta="center">
            {strings.discussion.allMembers.title}
          </CobuText>
          <CobuText variant="body" color="$white" fontSize='$0' fontWeight="bold" ta="center">
            {totalCount} {strings.discussion.allMembers.subtitle}{totalCount > 1 ? 's' : ''}
          </CobuText>
        </CobuStack>
      </CobuStack>
      */}

      {/* Search */}
      <CobuStack layout="horizontal" ai="center" p="$4" gap="$3">
        <SearchIcon style={{ width: 20, height: 20, color: theme.colorMuted?.val }} />
        <input
          style={{
            flex: 1,
            fontSize: 16,
            color: '#333',
            padding: 0,
            border: 'none',
            outline: 'none',
            background: 'transparent',
          }}
          value={searchTerm}
          onChange={(e) => setSearchTerm((e.target as HTMLInputElement).value)}
          placeholder={strings.ui.modal.searchPeople}
        />
      </CobuStack>

      {/* Members Label */}
      <CobuStack px="$4" pt="$4" bg="$white">
        <CobuText variant="body" fontSize='$2' fontWeight="bold" color="$colorMuted" style={{ letterSpacing: 0.5 }}>
          {strings.ui.allMembers.membersLabel}
        </CobuText>
      </CobuStack>
    </CobuStack>
  );

  const renderMemberItem = ({ item }: { item: Member }) => (
    <MemberItem
      member={item}
      onUserPress={handleUserPress}
      currentUserId={currentUserId}
      enableDM={showDM && enableDM}
      onDM={handleDMPress}
    />
  );

  const renderEmptyState = () => (
    <CobuStack f={1} ai="center" jc="center" p="$4">
      <CobuText variant="body" color="$colorMuted" ta="center">
        {searchTerm ? strings.ui.allMembers.noResultsSearch : strings.ui.allMembers.noResults}
      </CobuText>
    </CobuStack>
  );

  return (
    <CobuStack f={1}>
      {/* Header */}
      {renderHeader()}

      {/* Members List */}
      <CobuStack f={1} bg="$white" gap="$0">
        {filteredMembers.length === 0 ? (
          renderEmptyState()
        ) : (
          <Virtuoso
            data={filteredMembers}
            computeItemKey={(index, item) => item.uuid ?? String(index)}
            itemContent={(index, item) => (
              <CobuStack px={0}>{renderMemberItem({ item })}</CobuStack>
            )}
            overscan={400}
            style={{ flex: 1, width: '100%', height: 'calc(100vh - 180px)' }}
          />
        )}
      </CobuStack>

      {/* CobuAlert for DM unavailability */}
      <CobuAlert
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertTitle}
        description={alertDescription}
        acceptText={strings.common.buttonLabels.ok}
      />
    </CobuStack>
  );
}